import { useEffect, useId, useRef, useState } from 'react'
import { VisuallyHidden } from '@n26/nxd'
import { ChevronsUpDownIcon } from '@/shared/ui/icons'
import styles from './MultiSelectFilter.module.css'

type MultiSelectFilterProps = {
  label: string
  options: readonly string[]
  selected: string[]
  onToggle: (value: string) => void
  /** Adds a box narrowing the options by what is typed. */
  searchable?: boolean
}

/** A dropdown of checkboxes: the count selected shows on its button. */
export const MultiSelectFilter = ({
  label,
  options,
  selected,
  onToggle,
  searchable = false,
}: MultiSelectFilterProps) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()

  // Closes on a click elsewhere or Escape (which hands focus back to the button).
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      buttonRef.current?.focus()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const needle = query.trim().toLowerCase()
  const shown = needle
    ? options.filter(option => option.toLowerCase().includes(needle))
    : options

  return (
    <div ref={rootRef} className={styles.group}>
      <button
        ref={buttonRef}
        type='button'
        className={`${styles.trigger} ${selected.length > 0 ? styles.active : ''}`}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen(current => !current)}
      >
        {label}
        {selected.length > 0 && (
          <span className={styles.count}>
            {selected.length}
            <VisuallyHidden> selected</VisuallyHidden>
          </span>
        )}
        <ChevronsUpDownIcon />
      </button>
      {open && (
        <div
          id={panelId}
          role='group'
          aria-label={label}
          className={styles.panel}
        >
          {searchable && (
            <input
              type='search'
              className={styles.search}
              placeholder={`Search ${label.toLowerCase()}…`}
              aria-label={`Search ${label.toLowerCase()}`}
              value={query}
              onChange={event => setQuery(event.target.value)}
            />
          )}
          {shown.length === 0 ? (
            <p className={styles.empty}>No values</p>
          ) : (
            <ul className={styles.options}>
              {shown.map(option => (
                <li key={option}>
                  <label className={styles.option}>
                    <input
                      type='checkbox'
                      checked={selected.includes(option)}
                      onChange={() => onToggle(option)}
                    />
                    {option}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
